import { useMemo, useState } from 'react';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAgreements } from '@/lib/hooks';
import { format } from 'date-fns';
import { Loader2, TrendingUp, DollarSign, Users, AlertCircle, Calendar, Activity } from 'lucide-react';

// =============================================
// TYPES
// =============================================

interface MembershipMetrics {
  active_count: number;
  mrr: number;
  arr: number;
  churn_rate: number;
  visits_owed: number;
  upcoming_renewals: number;
}

interface Filters {
  source: string;
  status: string;
  billingCycle: string;
  search: string;
}

// =============================================
// UTILITY FUNCTIONS
// =============================================

const formatCurrency = (cents: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
};

const getStatusBadgeClass = (status: string): string => {
  const statusMap: Record<string, string> = {
    active: 'bg-emerald-500/10 text-emerald-400 border-emerald-500',
    cancelled: 'bg-red-500/10 text-red-400 border-red-500',
    suspended: 'bg-amber-500/10 text-amber-400 border-amber-500',
    expired: 'bg-gray-500/10 text-gray-400 border-gray-500',
  };
  return statusMap[status] || 'bg-blue-500/10 text-blue-400 border-blue-500';
};

const getSourceBadgeClass = (source: string): string => {
  return source?.includes('successware')
    ? 'bg-orange-500/10 text-orange-400'
    : 'bg-green-500/10 text-green-400';
};

// =============================================
// CUSTOM HOOK FOR METRICS (RPC CALL)
// =============================================

const useMembershipMetrics = () => {
  return useQuery<MembershipMetrics>({
    queryKey: ['membership-metrics'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_membership_metrics', {
        p_company_ids: null,
      });
      if (error) throw error;
      return data as MembershipMetrics;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// =============================================
// METRIC CARD COMPONENT
// =============================================

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
}

const MetricCard = ({ title, value, icon }: MetricCardProps) => (
  <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
    <div className="flex items-center gap-2 text-slate-300 mb-2">
      {icon}
      <span className="text-sm font-medium">{title}</span>
    </div>
    <div className="text-2xl font-semibold text-white">{value}</div>
  </div>
);

// =============================================
// MAIN COMPONENT
// =============================================

const MembershipsPage = () => {
  const [filters, setFilters] = useState<Filters>({
    source: 'all',
    status: 'all',
    billingCycle: 'all',
    search: '',
  });

  // Fetch all agreements using React Query hook (cached for 5 minutes)
  const { data: allAgreements = [], isLoading } = useAgreements();
  
  // Fetch metrics (cached for 2 minutes)
  const { data: metrics, isLoading: metricsLoading } = useMembershipMetrics();

  // Client-side filtering of cached data (no additional queries!)
  const filteredAgreements = useMemo(() => {
    let filtered = [...allAgreements];

    // Source filter
    if (filters.source !== 'all') {
      filtered = filtered.filter(agreement => 
        agreement.source_system?.includes(filters.source) || false
      );
    }

    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(agreement => agreement.status === filters.status);
    }

    // Billing cycle filter
    if (filters.billingCycle !== 'all') {
      filtered = filtered.filter(agreement => 
        agreement.billing_frequency === filters.billingCycle
      );
    }

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(agreement =>
        agreement.agreement_number?.toLowerCase().includes(searchLower) ||
        agreement.customer_id?.toLowerCase().includes(searchLower)
      );
    }

    // Limit to 100 for performance
    return filtered.slice(0, 100);
  }, [allAgreements, filters]);

  // Table columns
  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        header: 'Agreement #',
        accessorKey: 'agreement_number',
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium text-slate-100">{row.original.agreement_number || row.original.id}</span>
            <span className={`mt-1 inline-flex w-fit items-center rounded-full border px-2 py-0.5 text-xs ${getSourceBadgeClass(row.original.source_system)}`}>
              {row.original.source_system?.includes('successware') ? 'SW' : 'Native'}
            </span>
          </div>
        ),
      },
      {
        header: 'Customer ID',
        accessorKey: 'customer_id',
        cell: ({ getValue }) => (
          <span className="text-sm text-slate-200">{getValue<string>() || 'N/A'}</span>
        ),
      },
      {
        header: 'Type',
        accessorKey: 'agreement_type',
        cell: ({ getValue }) => (
          <span className="text-sm capitalize text-slate-200">{getValue<string>() || 'N/A'}</span>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => (
          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${getStatusBadgeClass(row.original.status)}`}>
            {row.original.status}
          </span>
        ),
      },
      {
        header: 'Billing',
        accessorKey: 'billing_frequency',
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="text-sm capitalize text-slate-200">{row.original.billing_frequency || '—'}</span>
            <span className="text-xs font-medium text-emerald-400">
              {row.original.premium_amount ? formatCurrency(row.original.premium_amount * 100) : '—'}
            </span>
          </div>
        ),
      },
      {
        header: 'Start Date',
        accessorKey: 'start_date',
        cell: ({ getValue }) => {
          const value = getValue<string | null>();
          return value ? (
            <span className="text-sm text-slate-300">{format(new Date(value), 'MMM d, yyyy')}</span>
          ) : (
            <span className="text-sm text-slate-500">—</span>
          );
        },
      },
      {
        header: 'Renewal Date',
        accessorKey: 'renewal_date',
        cell: ({ getValue }) => {
          const value = getValue<string | null>();
          return value ? (
            <span className="text-sm text-slate-300">{format(new Date(value), 'MMM d, yyyy')}</span>
          ) : (
            <span className="text-sm text-slate-500">—</span>
          );
        },
      },
      {
        header: 'Auto Renew',
        accessorKey: 'auto_renew',
        cell: ({ getValue }) => {
          const value = getValue<boolean>();
          return (
            <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${value ? 'bg-green-500/10 text-green-400 border-green-500' : 'bg-gray-500/10 text-gray-400 border-gray-500'}`}>
              {value ? 'Yes' : 'No'}
            </span>
          );
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data: filteredAgreements,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-100">Memberships & Agreements</h1>
          <p className="text-sm text-slate-400">
            Comprehensive dashboard for SuccessWare and native memberships
          </p>
        </div>
        <button
          type="button"
          className="rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-white shadow hover:bg-emerald-600"
        >
          Create Membership
        </button>
      </header>

      {/* Key Metrics */}
      {metricsLoading ? (
        <div className="flex h-32 items-center justify-center text-slate-400">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading metrics...
        </div>
      ) : metrics ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <MetricCard
            title="Active"
            value={metrics.active_count.toLocaleString()}
            icon={<Users size={16} />}
          />
          <MetricCard
            title="MRR"
            value={formatCurrency(Math.round(metrics.mrr * 100))}
            icon={<DollarSign size={16} />}
          />
          <MetricCard
            title="ARR"
            value={formatCurrency(Math.round(metrics.arr * 100))}
            icon={<TrendingUp size={16} />}
          />
          <MetricCard
            title="Churn Rate"
            value={`${metrics.churn_rate}%`}
            icon={<AlertCircle size={16} />}
          />
          <MetricCard
            title="Renewals Due"
            value={metrics.upcoming_renewals.toLocaleString()}
            icon={<Calendar size={16} />}
          />
          <MetricCard
            title="Visits Owed"
            value={metrics.visits_owed.toLocaleString()}
            icon={<Activity size={16} />}
          />
        </div>
      ) : null}

      {/* Filters */}
      <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
        <div className="grid gap-4 md:grid-cols-4">
          <div>
            <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-slate-300">Source</label>
            <select
              value={filters.source}
              onChange={(e) => setFilters({ ...filters, source: e.target.value })}
              className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
            >
              <option value="all">All Sources</option>
              <option value="successware">SuccessWare</option>
              <option value="native">Native</option>
            </select>
          </div>
          <div>
            <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-slate-300">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="cancelled">Cancelled</option>
              <option value="suspended">Suspended</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          <div>
            <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-slate-300">Billing Cycle</label>
            <select
              value={filters.billingCycle}
              onChange={(e) => setFilters({ ...filters, billingCycle: e.target.value })}
              className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
            >
              <option value="all">All Cycles</option>
              <option value="monthly">Monthly</option>
              <option value="annual">Annual</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </div>
          <div>
            <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-slate-300">Search</label>
            <input
              type="text"
              placeholder="Agreement #, Customer ID..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder-slate-500"
            />
          </div>
        </div>
      </div>

      {/* Main Table */}
      <div className="rounded-lg border border-slate-800 bg-slate-900/60">
        {isLoading ? (
          <div className="flex h-48 items-center justify-center text-slate-400">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading agreements...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-800">
              <thead className="bg-slate-900/80">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-300"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-slate-800">
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-800/40">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-4 py-3 text-sm text-slate-200">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
                {!filteredAgreements.length && (
                  <tr>
                    <td colSpan={columns.length} className="px-4 py-16 text-center text-sm text-slate-400">
                      No memberships or agreements match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary */}
      {filteredAgreements.length > 0 && (
        <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
          <p className="text-sm text-slate-400">
            Showing {filteredAgreements.length} membership{filteredAgreements.length !== 1 ? 's' : ''}{' '}
            {filters.status !== 'all' && `with status: ${filters.status}`}
          </p>
        </div>
      )}
    </div>
  );
};

export default MembershipsPage;
